import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'

import Form from './components/FormSellerPlate/index.js'
import VehicleConfirmation from './components/VehicleConfirmation/index.js'
import VehicleSelection from './components/VehicleSelection/index.js'
import {usePublishMyAdWithPlateAnalyticsTrack} from './hooks/usePublishMyAdWithPlateAnalyticsTrack.js'
import {BASE_CLASS_NAME} from './config.js'

const StepFormSellerPlate = ({h1, onSelectVehicle, pageName}) => {
  const {domain} = useContext(Context)
  const router = useRouter()

  const [isLoading, setIsLoading] = useState(false)
  const [plateVehicleResults, setPlateVehicleResults] = useState()

  const {trackPageViewed} = usePublishMyAdWithPlateAnalyticsTrack({pageName})

  useMount(() => {
    trackPageViewed()
  })

  const config = domain.get('config')
  const urlStatics = config.get('urlStatics')
  const {PUBLISH_MY_AD_PATH_PATTERN} = config.get('page')

  const isPlateVehicleResults = Boolean(plateVehicleResults)
  const isOnlyOneVehicleResult = Boolean(plateVehicleResults?.vehicles.length === 1)
  const areManyVehicleResults = Boolean(plateVehicleResults?.vehicles.length > 1)

  const handleOnFormSubmit = response => {
    setPlateVehicleResults(response)
  }

  const contentClassName = cx(`${BASE_CLASS_NAME}-content`, {
    [`${BASE_CLASS_NAME}-content--fullWidth`]: areManyVehicleResults
  })

  const imageWrapperClassName = `${BASE_CLASS_NAME}-imageWrapper`

  const getImageUrl = () => {
    return `${urlStatics}images/illustrations/${isOnlyOneVehicleResult ? `car-documentation` : `introduce-plate`}.svg`
  }

  const handleDenySelection = () => {
    router.push(`${PUBLISH_MY_AD_PATH_PATTERN}?withPlate=true&withPlateStatus=plateListNotMyCar`)
  }

  const handleSelectVehicle = async ({plateStatus = 'plateRecommendedPrice', vehicleSelected = 0} = {}) => {
    setIsLoading(true)

    const [draftError, draftResponse] = await domain.get('obtain_draft_ad_publisher_use_case').execute()

    if (draftError) {
      setIsLoading(false)
      return
    }

    const {
      data: {id: adId}
    } = draftResponse

    const {
      bodyTypeId,
      doors,
      fuelTypeId,
      makeId,
      modelId,
      plateRegistrationDate,
      plateRegistrationYear,
      transmissionTypeId,
      versionId
    } = plateVehicleResults.vehicles[vehicleSelected]

    const fields = {
      bodyTypeDoors: `${bodyTypeId}-${doors}`,
      carPlate: plateVehicleResults.plate,
      fuelId: fuelTypeId,
      km: plateVehicleResults.km,
      makeId,
      modelId,
      plateRegistrationDate,
      publishInMA: true,
      transmissionTypeId,
      versionId,
      year: plateRegistrationYear
    }

    await domain.get('edit_draft_ad_publisher_use_case').execute({
      fields,
      adId
    })

    const url = `${PUBLISH_MY_AD_PATH_PATTERN}?withPlate=true&withPlateStatus=${plateStatus}&plateVehicleSelected=${vehicleSelected}`

    onSelectVehicle({adId, destinationUrl: url, fields})
  }

  return (
    <Layout.Container isFullWidth>
      <div className={BASE_CLASS_NAME}>
        <main className={contentClassName}>
          {!isPlateVehicleResults && <Form title={h1} onSubmit={handleOnFormSubmit} />}
          {isOnlyOneVehicleResult && (
            <VehicleConfirmation
              isLoading={isLoading}
              onDenySelection={handleDenySelection}
              onSelectVehicle={handleSelectVehicle}
              vehicleData={{
                plate: plateVehicleResults.plate,
                km: plateVehicleResults.km,
                ...plateVehicleResults.vehicles[0]
              }}
            />
          )}
          {areManyVehicleResults && (
            <VehicleSelection
              isLoading={isLoading}
              km={plateVehicleResults.km}
              onDenySelection={handleDenySelection}
              onSelectVehicle={handleSelectVehicle}
              plate={plateVehicleResults.plate}
              vehicles={plateVehicleResults.vehicles}
            />
          )}
        </main>
        {(!isPlateVehicleResults || isOnlyOneVehicleResult) && (
          <aside className={imageWrapperClassName}>
            <AtomImage aria-hidden="true" role="presentation" src={getImageUrl()} />
          </aside>
        )}
      </div>
    </Layout.Container>
  )
}

StepFormSellerPlate.displayName = 'StepFormSellerPlate'

StepFormSellerPlate.propTypes = {
  h1: PropTypes.string.isRequired,
  onSelectVehicle: PropTypes.func.isRequired,
  pageName: PropTypes.string.isRequired
}

export default StepFormSellerPlate
